import { m as motion } from 'framer-motion';
import { css, cx, mediaQueries } from '@eti/styles';
import { useProperty } from '@eti/providers';
import type { ReactNode } from 'react';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import { SEARCH_RESULT_SIDEBAR_HEIGHT_EVENT } from '../../../../constants/customEventConstants';
import { RESULT_PAGE_VERSION_3 } from '../../constants/resultConstants';
import FilterHeader from './v1/FilterHeader';
import FilterHeaderV3 from './v3/FilterHeader';

const filterWrapperStyles = css`
  flex: 0 0 100%;
  max-width: 100%;

  @media ${mediaQueries.medium.up} {
    flex: 0 0 calc(50% - 15px);
    max-width: calc(50% - 15px);
  }
`;

const motionVariants = {
  show: {
    height: 'auto',
    transitionEnd: { display: 'block', overflow: 'visible' },
  },
  hide: {
    overflow: 'hidden',
    height: 0,
    transitionEnd: { display: 'none' },
  },
};

interface FilterProps {
  children: ReactNode;
  className: string;
  destinationName?: string;
  isInitiallyExpanded: boolean;
  isSelected?: boolean;
  isToggleable: boolean;
  label?: string;
  name: string;
  onReset?: () => void;
  originName?: string;
  shouldShowResetButton?: boolean;
}

const Filter = ({
  children,
  className,
  destinationName = '',
  isInitiallyExpanded = false,
  isSelected,
  isToggleable = true,
  label = '',
  name,
  onReset,
  originName = '',
}: FilterProps) => {
  const { p } = useProperty();
  const { isOpen, handleToggle } = useToggle(
    isSelected || isInitiallyExpanded,
    SEARCH_RESULT_SIDEBAR_HEIGHT_EVENT,
    true,
  );

  const resultPageVersion = String(p('Result.Page.Version'));
  const commonPropsForFilterHeader = {
    destinationName,
    isOpen,
    isToggleable,
    label,
    name,
    onClick: handleToggle,
    onReset,
    originName,
  };

  let filterHeader;
  switch (resultPageVersion) {
    case RESULT_PAGE_VERSION_3:
      filterHeader = <FilterHeaderV3 {...commonPropsForFilterHeader} />;
      break;
    default:
      filterHeader = <FilterHeader {...commonPropsForFilterHeader} />;
      break;
  }

  return (
    <section className={cx(filterWrapperStyles, className)}>
      {filterHeader}
      <motion.div
        animate={!isToggleable || isOpen ? 'show' : 'hide'}
        data-testid={`resultPage-${name}Filter-content`}
        initial={false}
        transition={{ type: 'tween', duration: 0.3 }}
        variants={motionVariants}
      >
        {children}
      </motion.div>
    </section>
  );
};

export default Filter;
